import { createContext, useContext, useRef, useEffect, ReactNode } from "react";

interface ItemCountContextValue {
  mainItemCount: number;
  additionalItemCount: number;
  incrementMainCount: () => number;
  incrementAdditionalCount: () => number;
  resetCounts: () => void;
}

export const ItemCountContext = createContext<ItemCountContextValue | null>(
  null
);

export function useItemCount() {
  const context = useContext(ItemCountContext);
  if (!context) {
    throw new Error("useItemCount must be used within an ItemCountProvider");
  }
  return context;
}

interface ItemCountProviderProps {
  children: ReactNode;
}

export function ItemCountProvider({ children }: ItemCountProviderProps) {
  const mainCountRef = useRef(0);
  const additionalCountRef = useRef(0);

  // Reset counters on each render
  useEffect(() => {
    mainCountRef.current = 0;
    additionalCountRef.current = 0;
  });

  const incrementMainCount = () => {
    mainCountRef.current += 1;
    return mainCountRef.current;
  };

  const incrementAdditionalCount = () => {
    additionalCountRef.current += 1;
    return additionalCountRef.current;
  };

  const resetCounts = () => {
    mainCountRef.current = 0;
    additionalCountRef.current = 0;
  };

  const value: ItemCountContextValue = {
    mainItemCount: mainCountRef.current,
    additionalItemCount: additionalCountRef.current,
    incrementMainCount,
    incrementAdditionalCount,
    resetCounts,
  };

  return (
    <ItemCountContext.Provider value={value}>
      {children}
    </ItemCountContext.Provider>
  );
}
